import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { hasSubmitFailed } from 'redux-form';
import _ from 'lodash';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ApplyPageForm from './ApplyPageForm/ApplyPageForm';
import EscapeHatch from '../../components/EscapeHatch/EscapeHatch';
import { FLOW_TYPE, FORM_ID } from '../../../framework/App/AppConstants';
import { trackTransition } from '../../../framework/store/FPTI/FPTIActions';
import { triggerEnsightenEvent } from '../../../framework/store/Ensighten/EnsightenActions';
import { ENSIGHTEN_APP_START, ENSIGHTEN_APP_SOFT_DECLINED } from '../../../framework/store/Ensighten/EnsightenConstants';
import NotificationBanner, { NOTIFICATION_TYPE } from '../../../shared-components/NotificationBanner/NotificationBanner';
import '../../styles/base-gb.less';

const LOCALE_PREFIX = 'GB/pages/ApplyPage/ApplyPage';

export class ApplyPage extends Component {
    static propTypes = {
        appStatus: PropTypes.string,
        flow: PropTypes.oneOf(Object.values(FLOW_TYPE)).isRequired,
        isCrossBorderWithInstallments: PropTypes.bool,
        hasSoftDeclineMsg: PropTypes.bool,
        hasSubmitFailed: PropTypes.bool,
        intl: intlShape.isRequired,
        invalidAddressMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        messages: PropTypes.object.isRequired,
        showEscapeHatch: PropTypes.bool,
        showValidationSoftDecline: PropTypes.bool,
        softDeclineCustomMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        onComponentDidMount: PropTypes.func.isRequired
    };

    static defaultProps = {
        onComponentDidMount: () => {},
        hasSoftDeclineMsg: false,
        hasSubmitFailed: false,
        showEscapeHatch: false,
        showValidationSoftDecline: false,
        isCrossBorderWithInstallments: false
    };

    componentDidMount() {
        this.props.onComponentDidMount(this.props);
    }

    getNotifications = () => {
        const {
            flow,
            hasSoftDeclineMsg,
            hasSubmitFailed,
            intl,
            invalidAddressMsg,
            messages,
            showValidationSoftDecline,
            softDeclineCustomMsg,
            isCrossBorderWithInstallments
        } = this.props;
        const isTesla = (flow === FLOW_TYPE.TESLA);
        const ternaryUtil = (condition, target, fallback) => condition ? target : fallback || '';
        const softDeclineMsg = ternaryUtil(isTesla, 'softDeclineAddressMessage-tesla', 'softDeclineAddressMessage');
        let notificationMessage;
        let notificationType;
        let teIconClasses;

        if (isCrossBorderWithInstallments) {
            notificationMessage = intl.formatMessage({ id: `${LOCALE_PREFIX}.applyForBmlUK.selectedOfferUnavailable` });
            notificationType = NOTIFICATION_TYPE.WARNING;
            teIconClasses = ternaryUtil(isTesla, 'cw_icon cw_icon-small cw_icon-attention-large');
        }
        if (hasSubmitFailed) {
            notificationMessage = intl.formatMessage({ id: `${LOCALE_PREFIX}.applyForBmlUK.fieldError` });
            notificationType = ternaryUtil(isTesla, NOTIFICATION_TYPE.ERROR, NOTIFICATION_TYPE.WARNING);
            teIconClasses = ternaryUtil(isTesla, 'cw_icon cw_icon-small cw_icon-critical-large');
        }
        if (invalidAddressMsg) {
            notificationMessage = intl.formatMessage({ id: `${LOCALE_PREFIX}.applyForBmlUK.${softDeclineMsg}` });
            notificationType = NOTIFICATION_TYPE.WARNING;
            teIconClasses = ternaryUtil(isTesla, 'cw_icon cw_icon-small cw_icon-attention-large');
        }
        if (showValidationSoftDecline || hasSoftDeclineMsg) {
            const defaultSoftDeclineMessage = intl.formatMessage({ id: `${LOCALE_PREFIX}.applyForBmlUK.${softDeclineMsg}` });
            notificationMessage = defaultSoftDeclineMessage;
            notificationType = NOTIFICATION_TYPE.WARNING;
            teIconClasses = ternaryUtil(isTesla, 'cw_icon cw_icon-small cw_icon-attention-large');

            if (softDeclineCustomMsg) {
                notificationMessage = _.get(
                    messages,
                    [LOCALE_PREFIX, 'applyForBmlUK', 'customSoftDeclineMessages', softDeclineCustomMsg],
                    defaultSoftDeclineMessage
                );
            }
        }

        return notificationType ? <NotificationBanner message={notificationMessage} type={notificationType} iconClasses={teIconClasses} /> : null;
    };

    render() {
        const {
            flow,
            intl,
            showEscapeHatch,
            isCrossBorderWithInstallments
        } = this.props;
        const isXOFlow = (flow === FLOW_TYPE.HERMES || flow === FLOW_TYPE.PROX);

        const offerTerms = isCrossBorderWithInstallments ? (<div id="offerTerms">
            <h2 className="alpha"><FormattedMessage id={`${LOCALE_PREFIX}.applyForBmlUK.offerTermsHeader`} /></h2>
            <p><FormattedMessage id={`${LOCALE_PREFIX}.applyForBmlUK.offerTerms`} /></p>
        </div>)
            : '';

        const offerTermsConditions = isCrossBorderWithInstallments ? (<div id="offerTermsCondition" className="secondary">
            <FormattedMessage id={`${LOCALE_PREFIX}.applyForBmlUK.offerTermsConditions`} />
        </div>)
            : '';

        return (
            <div className="outerWrapper gb-wrapper">
                <div id="wrapper">
                    <div id="main">
                        <div id="ApplyPage">
                            {(flow !== FLOW_TYPE.TESLA) && <Header singleColumnLayout={false} />}
                            <div id="content">
                                <div id="content-child" className="row-fluid table-layout">
                                    <div id="content-left" className="span14 table-cell pull-left">
                                        <section className="contentSection">
                                            <div id="notifications">
                                                {this.getNotifications()}
                                            </div>
                                            {offerTerms}
                                            <ApplyPageForm />
                                            {offerTermsConditions}
                                        </section>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer showCancelUrl={isXOFlow} />

                {showEscapeHatch && (
                    <EscapeHatch
                        headerText={intl.formatMessage({ id: `${LOCALE_PREFIX}.escapeHatch.header` })}
                        bodyText1={intl.formatMessage({ id: `${LOCALE_PREFIX}.escapeHatch.body1` })}
                        bodyText2={intl.formatMessage({ id: `${LOCALE_PREFIX}.escapeHatch.body2` })}
                        continueText={intl.formatMessage({ id: `${LOCALE_PREFIX}.escapeHatch.continue` })}
                        cancelText={intl.formatMessage({ id: `${LOCALE_PREFIX}.escapeHatch.cancel` })}
                    />
                )}

            </div>
        );
    }
}

export const mapStateToProps = state => ({
    appStatus: _.get(state, 'serverData.appStatus'),
    flow: _.get(state, 'serverData.flow'),
    isCrossBorderWithInstallments: _.get(state, 'serverData.isCrossBorderWithInstallments'),
    hasSoftDeclineMsg: _.get(state, 'serverData.softDeclineMsg'),
    hasSubmitFailed: hasSubmitFailed(FORM_ID)(state),
    invalidAddressMsg: _.get(state, 'serverData.invalidAddressMsg'),
    messages: _.get(state, 'serverData.messages'),
    showEscapeHatch: _.get(state, 'escapeHatch.showEscapeHatch'),
    showValidationSoftDecline: _.get(state, 'serverData.showValidationSoftDecline'),
    softDeclineCustomMsg: _.get(state, 'serverData.softDeclineCustomMsg')
});

export const mapDispatchToProps = dispatch => ({
    onComponentDidMount(props) {
        const {
            appStatus,
            showValidationSoftDecline,
            hasSoftDeclineMsg
        } = props;
        const extra = {};
        if (showValidationSoftDecline || hasSoftDeclineMsg) {
            extra.ppc_status = appStatus;
            dispatch(triggerEnsightenEvent(ENSIGHTEN_APP_SOFT_DECLINED));
        } else {
            dispatch(triggerEnsightenEvent(ENSIGHTEN_APP_START));
        }
        dispatch(trackTransition('pp_credit_applypage', 'prepare_pp_credit_applypage', extra));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ApplyPage));
